import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Divider,
  Chip,
  IconButton,
  InputAdornment,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { ApplicationStatus } from '../JobCard/JobStatusCard';

export interface CandidateFilters {
  // Basic
  search: string;           // name or email
  status: string;           // ApplicationStatus value as string, '' = all
  minScore: string;
  maxScore: string;
  dateAppliedFrom: string;
  dateAppliedTo: string;

  // Education / Experience
  degreeLevel: string;
  fieldOfStudy: string;
  graduationDateFrom: string;
  graduationDateTo: string;
  minYearsExp: string;

  // Eligibility
  immigrationStatus: string;
  workEligibility: string;   // 'yes' | 'no' | ''
  ageEligibility: string;    // 'yes' | 'no' | ''
  availableBeforeJuly4: string; // 'yes' | 'no' | ''
}

export const defaultFilters: CandidateFilters = {
  search: '',
  status: '',
  minScore: '',
  maxScore: '',
  dateAppliedFrom: '',
  dateAppliedTo: '',
  degreeLevel: '',
  fieldOfStudy: '',
  graduationDateFrom: '',
  graduationDateTo: '',
  minYearsExp: '',
  immigrationStatus: '',
  workEligibility: '',
  ageEligibility: '',
  availableBeforeJuly4: '',
};

interface CandidateFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: CandidateFilters) => void;
  activeFilterCount: number;
}

const statusOptions = [
  { label: 'All Statuses', value: '' },
  { label: 'Pending Review', value: String(ApplicationStatus.PENDING_REVIEW) },
  { label: 'Pending Decision', value: String(ApplicationStatus.PENDING_DECISION) },
  { label: 'Shortlisted', value: String(ApplicationStatus.SHORTLISTED) },
  { label: 'Accepted', value: String(ApplicationStatus.ACCEPTED) },
  { label: 'Not Selected', value: String(ApplicationStatus.REJECTED) },
];

const degreeLevelOptions = [
  { label: 'Any', value: '' },
  { label: 'High School / GED', value: 'high school' },
  { label: "Associate's", value: 'associate' },
  { label: "Bachelor's", value: 'bachelor' },
  { label: "Master's", value: 'master' },
  { label: 'PhD / Doctorate', value: 'phd' },
  { label: 'Certificate / Diploma', value: 'certificate' },
];

const immigrationOptions = [
  { label: 'Any', value: '' },
  { label: 'Citizen', value: 'citizen' },
  { label: 'Permanent Resident', value: 'permanent resident' },
  { label: 'Refugee', value: 'refugee' },
];

const yesNoOptions = [
  { label: 'Any', value: '' },
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ mt: 2.5, mb: 1 }}>
    <Typography
      variant="overline"
      sx={{
        fontWeight: 700,
        fontSize: '0.72rem',
        letterSpacing: '0.08em',
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

const CandidateFilterModal: React.FC<CandidateFilterModalProps> = ({
  open,
  onClose,
  onApply,
  activeFilterCount,
}) => {
  const [filters, setFilters] = useState<CandidateFilters>(defaultFilters);

  const handleChange = useCallback(
    (field: keyof CandidateFilters) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFilters(prev => ({ ...prev, [field]: e.target.value }));
      },
    [],
  );

  const handleReset = () => {
    setFilters(defaultFilters);
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const activeCount = Object.entries(filters).filter(
    ([, v]) => v !== '' && v !== null && v !== undefined,
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700}>
            Filter Candidates
          </Typography>
          {activeCount > 0 && (
            <Chip
              label={`${activeCount} active`}
              size="small"
              color="primary"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 1.5 }}>
        {/* ══════════════ BASIC ══════════════ */}
        <SectionHeader label="Basic" />

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          label="Search by name or email"
          value={filters.search}
          onChange={handleChange('search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Status */}
        <TextField
          select
          fullWidth
          size="small"
          label="Status"
          value={filters.status}
          onChange={handleChange('status')}
          sx={{ mb: 2 }}
        >
          {statusOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Score range */}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Min Score"
              type="number"
              value={filters.minScore}
              onChange={handleChange('minScore')}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Max Score"
              type="number"
              value={filters.maxScore}
              onChange={handleChange('maxScore')}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        {/* Date applied */}
        <Grid container spacing={1.5} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Applied from"
              type="date"
              value={filters.dateAppliedFrom}
              onChange={handleChange('dateAppliedFrom')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Applied to"
              type="date"
              value={filters.dateAppliedTo}
              onChange={handleChange('dateAppliedTo')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* ══════════════ EDUCATION / EXPERIENCE ══════════════ */}
        <SectionHeader label="Education / Experience" />

        {/* Degree level */}
        <TextField
          select
          fullWidth
          size="small"
          label="Degree level"
          value={filters.degreeLevel}
          onChange={handleChange('degreeLevel')}
          sx={{ mb: 2 }}
        >
          {degreeLevelOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Field of study */}
        <TextField
          fullWidth
          size="small"
          label="Field of study (e.g. Computer Science)"
          value={filters.fieldOfStudy}
          onChange={handleChange('fieldOfStudy')}
          sx={{ mb: 3 }}
        />

        {/* Graduation date */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Graduation from"
              type="date"
              value={filters.graduationDateFrom}
              onChange={handleChange('graduationDateFrom')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Graduation to"
              type="date"
              value={filters.graduationDateTo}
              onChange={handleChange('graduationDateTo')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Years of experience */}
        <TextField
          fullWidth
          size="small"
          label="Min years of experience"
          type="number"
          value={filters.minYearsExp}
          onChange={handleChange('minYearsExp')}
          inputProps={{ min: 0 }}
          sx={{ mb: 1 }}
        />

        {/* ══════════════ ELIGIBILITY ══════════════ */}
        <SectionHeader label="Eligibility" />

        {/* Immigration status */}
        <TextField
          select
          fullWidth
          size="small"
          label="Citizen / PR / Refugee"
          value={filters.immigrationStatus}
          onChange={handleChange('immigrationStatus')}
          sx={{ mb: 2 }}
        >
          {immigrationOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Legally allowed to work */}
        <TextField
          select
          fullWidth
          size="small"
          label="Legally allowed to work"
          value={filters.workEligibility}
          onChange={handleChange('workEligibility')}
          sx={{ mb: 2 }}
        >
          {yesNoOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Age 15-30 */}
        <TextField
          select
          fullWidth
          size="small"
          label="Age 15–30"
          value={filters.ageEligibility}
          onChange={handleChange('ageEligibility')}
          sx={{ mb: 2 }}
        >
          {yesNoOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Available before July 4 */}
        <TextField
          select
          fullWidth
          size="small"
          label="Available before July 4"
          value={filters.availableBeforeJuly4}
          onChange={handleChange('availableBeforeJuly4')}
          sx={{ mb: 1 }}
        >
          {yesNoOptions.map(o => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      {/* ── Footer actions ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          variant="outlined"
          color="inherit"
          sx={{ textTransform: 'none', borderColor: '#ccc', color: 'text.secondary' }}
        >
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="text" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            backgroundColor: '#1976d2',
            '&:hover': { backgroundColor: '#115293' },
          }}
        >
          Apply Filters
          {activeCount > 0 && ` (${activeCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CandidateFilterModal;
